import classnames from "classnames";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { IConsultantProfile, TCompletenessItemKey } from "@/data/consultant-profiles";
import { Flex, Tooltip } from "@/eworkitComponents";
import { ClickableSection } from "@/infrastructure/Route";

import { ResetAssessmentsModalButton } from "../Assessments/ResetAssessmentsModal";

import "./_card.scss";

export interface CardProps {
  itemKey: TCompletenessItemKey;
  additionalText: string;
  description: string;
  icon: string;
  isActive?: boolean;
  isDone?: boolean;
  onClick?: () => void;
  size?: "small" | "large";
  title: string;
  tooltip?: string;
  resetAllowed?: boolean;
  onUpdate: (profile: IConsultantProfile) => void;
}

const BASE_CLASS = "completeness-card";

export const Card: FC<CardProps> = memo(
  ({
    itemKey,
    additionalText,
    description,
    icon,
    isActive = false,
    isDone: done = true,
    onClick,
    size = "large",
    title,
    tooltip,
    resetAllowed,
    onUpdate,
  }) => {
    const { t } = useTranslation("session");
    const isLargeSize = size === "large";
    const handleClick = isActive ? onClick : undefined;
    const isDone = isActive ? false : done;

    return (
      <Flex className={`${BASE_CLASS}-wrapper`}>
        <ClickableSection
          borderRadius
          padding={[isLargeSize ? "xl" : "lg"]}
          direction="row"
          flex
          className={classnames(BASE_CLASS, {
            [`${BASE_CLASS}--is-active`]: isActive,
            [`${BASE_CLASS}--is-clickable`]: handleClick,
          })}
          onClick={handleClick}>
          <Flex grow={1} direction="column" innerMargin="sm" className={`${BASE_CLASS}-wrapper`}>
            <Header size={isLargeSize ? "lg" : "md"} weight="bold" color={isActive ? "black" : "grey1"}>
              <Flex direction="row">
                <Flex
                  grow={1}
                  className={classnames({
                    [`-not-active`]: !isActive,
                  })}>
                  {title}
                  <Icon icon="arrow_right" color="grey2" className={`-margin-left-sm`} />
                </Flex>
                {tooltip && (
                  <Flex align="center">
                    <Tooltip content={tooltip} placement="left">
                      <Flex align="center">
                        <Icon icon="info" size="md" color="grey1" />
                      </Flex>
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
            </Header>
            <Text className={`${BASE_CLASS}-desc`} color={isActive ? "black" : "grey1"}>
              {description}
            </Text>
            <Flex
              className={classnames({
                [`-not-active`]: !isActive,
              })}
              grow={1}
              justify="flex-end"
              direction="column">
              {isDone ? (
                <Header size={isLargeSize ? "lg" : "md"} weight="bold" color="primary">
                  <Icon icon="checkbox" className="-margin-right-xs" />
                  {t("CONSULTANT_PROFILES.COMPLETENESS.DONE")}
                </Header>
              ) : (
                <Header size={isLargeSize ? "lg" : "md"} weight="bolder" color="default">
                  {additionalText}
                </Header>
              )}
            </Flex>
          </Flex>
          {!isDone && (
            <div
              className={classnames(`${BASE_CLASS}-bg-icon-wrapper -position-absolute`, {
                [`-not-active`]: !isActive,
              })}>
              <Icon icon={icon} color="background-main" className="-position-absolute" />
            </div>
          )}
        </ClickableSection>
        {resetAllowed && (
          <Flex className={`${BASE_CLASS}-reset-button-wrapper`}>
            <ResetAssessmentsModalButton variant="link-danger" modalProps={{ assessments: [itemKey], onUpdate }}>
              {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.BUTTON_RESET")}
            </ResetAssessmentsModalButton>
          </Flex>
        )}
      </Flex>
    );
  },
);
